/**
 * LayoutV2 — Phase UX-A App Shell UI Refresh
 * 
 * Dark top nav with tabs, plan/verification badges, LandingPageNew design language.
 * Behind ENABLE_APP_SHELL_UI_V2 flag.
 */
import React, { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useLitpulseChromeHeight } from '../hooks/useLitpulseChromeHeight';
import { LITPULSE_DIGEST_SLOT_ID } from '../constants/litpulseChrome';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Home,
  Search,
  Users,
  Library,
  FileText,
  Bell,
  LogOut,
  Crown,
  ShieldCheck,
  ShieldAlert,
  Settings,
  ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { notificationsAPI } from '../api';
import { FeatureNavBar } from './shared/FeatureNavBar';

interface LayoutV2Props {
  children: ReactNode;
}

const NAV_TABS = [
  { path: '/library', label: 'Home', icon: Home, testId: 'nav-home-tab' },
  { path: '/inbox', label: 'Inbox', icon: Bell, testId: 'nav-inbox-tab', hasBadge: true },
];

const LayoutV2: React.FC<LayoutV2Props> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const chromeRef = useLitpulseChromeHeight<HTMLDivElement>();

  // Fetch unread count on mount and periodically
  useEffect(() => {
    if (!user) return;

    let interval: ReturnType<typeof setInterval> | null = null;

    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsAPI.getUnreadCount();
        setUnreadCount(response.unread_count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    const startPolling = () => {
      if (interval) return;
      fetchUnreadCount();
      interval = setInterval(fetchUnreadCount, 30000);
    };

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    if (!document.hidden) {
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  // Don't show navbar on landing or auth pages
  const isAuthPage = [
    '/',
    '/login',
    '/signup',
    '/verify-email',
    '/request-password-reset',
    '/reset-password',
  ].includes(location.pathname);

  if (isAuthPage || !user) {
    return <>{children}</>;
  }

  // Derive user status
  const isPremium = user?.plan_tier === 'premium' || user?.trial_active;
  const peerStatus = user?.peer_verification_status || 'none';
  const isVerified = peerStatus === 'verified';

  // Check if current path matches tab
  const isActiveTab = (tabPath: string) => {
    if (tabPath === '/library') {
      return location.pathname === '/library';
    }
    return location.pathname.startsWith(tabPath);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="layout-v2">
      <div ref={chromeRef} className="sticky top-0 z-50 bg-slate-900 shadow-md">
      {/* Dark Top Navigation Bar */}
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link to="/library" className="flex items-center gap-2" data-testid="nav-logo">
              <span className="text-lg font-bold text-white tracking-tight">LitCurrent</span>
            </Link>

            {/* Center: Nav Tabs */}
            <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
              {NAV_TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = isActiveTab(tab.path);
                return (
                  <button
                    key={tab.path}
                    onClick={() => navigate(tab.path)}
                    className={`relative flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors
                      ${isActive
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                      }`}
                    data-testid={tab.testId}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                    {tab.hasBadge && unreadCount > 0 && (
                      <Badge
                        className="absolute -top-1 -right-1 h-4 min-w-[16px] flex items-center justify-center p-0 text-[10px] bg-red-500 text-white border-0"
                        data-testid="nav-inbox-badge"
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Right: Badges + Menu */}
            <div className="flex items-center gap-2">
              {/* Plan Badge */}
              <button
                onClick={() => navigate('/plan')}
                className={`hidden md:flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                  ${isPremium
                    ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                data-testid="nav-plan-badge"
                title={isPremium ? 'Pro plan' : 'Free plan - Click to upgrade'}
              >
                <Crown className="h-3 w-3" />
                <span>{isPremium ? 'Pro' : 'Free'}</span>
              </button>

              {/* Verification Badge */}
              <button
                onClick={() => navigate('/verification')}
                className={`hidden md:flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                  ${isVerified
                    ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                data-testid="nav-verification-badge"
                title={isVerified ? 'Verified professional' : 'Unverified - Click to verify'}
              >
                {isVerified ? (
                  <ShieldCheck className="h-3 w-3" />
                ) : (
                  <ShieldAlert className="h-3 w-3" />
                )}
                <span>{isVerified ? 'Verified' : 'Unverified'}</span>
              </button>

              {/* Settings Dropdown — desktop */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden md:inline-flex text-slate-300 hover:text-white hover:bg-slate-800"
                    data-testid="nav-settings-menu"
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/preferences')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Preferences
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/plan')}>
                    <Crown className="h-4 w-4 mr-2" />
                    Plan
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/verification')}>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Professional Verification
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/phi-guidelines')}>
                    <ShieldAlert className="h-4 w-4 mr-2" />
                    PHI-Zero Policy
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile: Hamburger menu for tabs */}
              <div className="md:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-300 hover:text-white hover:bg-slate-800"
                      data-testid="nav-mobile-menu"
                    >
                      <span className="sr-only">Open menu</span>
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {NAV_TABS.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <DropdownMenuItem
                          key={tab.path}
                          onClick={() => navigate(tab.path)}
                          className={isActiveTab(tab.path) ? 'bg-slate-100' : ''}
                        >
                          <Icon className="h-4 w-4 mr-2" />
                          {tab.label}
                          {tab.hasBadge && unreadCount > 0 && (
                            <Badge className="ml-auto h-4 min-w-[16px] flex items-center justify-center p-0 text-[10px] bg-red-500 text-white border-0">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </Badge>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/community')}>
                      <Users className="h-4 w-4 mr-2" />
                      LitClub
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/preferences')}>
                      <Settings className="h-4 w-4 mr-2" />
                      Preferences
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/plan')}>
                      <Crown className="h-4 w-4 mr-2" />
                      Plan
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/verification')}>
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Verification
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Trial banner */}
      {user?.trial_active && !user?.has_subscription && (() => {
        const daysLeft = user.trial_expires_at
          ? Math.max(0, Math.ceil((new Date(user.trial_expires_at).getTime() - Date.now()) / 86400000))
          : 0;
        return (
          <div className="bg-amber-50 border-b border-amber-200 py-1.5 px-4 text-center" data-testid="trial-bar">
            <span className="text-xs text-amber-800">
              Pro trial: <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> remaining.{' '}
              <button onClick={() => navigate('/plan')} className="underline font-medium hover:text-amber-900">
                Subscribe
              </button>
            </span>
          </div>
        );
      })()}

      {/* Secondary Navigation - Feature Buttons */}
      <FeatureNavBar />
      <div id={LITPULSE_DIGEST_SLOT_ID} />
      </div>

      {/* Main Content */}
      <main>{children}</main>
    </div>
  );
};

export default LayoutV2;
